import { useCallback, useRef, useState } from "react";

/**
 * Tracks whether a scroll container is wider than its visible area.
 *
 * Attach the returned `ref` (a callback ref) to the element that scrolls
 * horizontally; read `hasHorizontalOverflow` to drive affordances such as a
 * pinned-column shadow. The observer attaches whenever the element mounts — so
 * it works even when the element appears after the first render (e.g. once an
 * empty state is replaced) — and re-measures on container resize (viewport
 * shrink) and window resize. Not on scroll, since scrolling never changes
 * whether the content overflows.
 */
export function useHorizontalOverflow() {
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  const ref = useCallback((element: HTMLDivElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;
    if (!element) return;

    const update = () => {
      setHasHorizontalOverflow(element.scrollWidth > element.clientWidth + 1);
    };
    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);
    window.addEventListener("resize", update);

    cleanupRef.current = () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", update);
    };
  }, []);

  return { ref, hasHorizontalOverflow };
}
